import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  MenuItem,
  Grid,
  InputAdornment,
  Autocomplete,
  Divider,
  CircularProgress,
  Skeleton,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  CheckCircleOutline as CheckCircleIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { toast } from 'react-toastify';
import { addClient } from '../../services/firebase';
import { ClientFormData, CaseType } from '../../types';
import { countries } from '../../utils/countries';

const phoneRegExp = /^(\+\d{1,3}[-.]?)?\d{3}[-.]?\d{3}[-.]?\d{4}$/;

const schema = yup.object().shape({
  fullName: yup
    .string()
    .required('Full name is required')
    .matches(/^[A-Za-z\s]+$/, 'Only letters and spaces are allowed')
    .max(100, 'Full name must be at most 100 characters'),
  phoneNumber: yup
    .string()
    .required('Phone number is required')
    .matches(phoneRegExp, 'Phone number is not valid'),
  email: yup
    .string()
    .required('Email is required')
    .email('Invalid email format'),
  countryOfOrigin: yup
    .string()
    .required('Country of origin is required'),
  initialCase: yup
    .object({
      type: yup
        .string()
        .oneOf(['Asylum', 'Work Permit', 'Naturalization', 'Adjustment Green Card'] as const)
        .required(),
      applicationDate: yup.string().required('Application date is required'),
      receiptNumber: yup
        .string()
        .required('Receipt number is required')
        .matches(/^[A-Za-z0-9-]+$/, 'Only alphanumeric characters and hyphens are allowed')
        .max(20, 'Receipt number must be at most 20 characters'),
      amountCharged: yup
        .number()
        .typeError('Amount is required')
        .required('Amount is required')
        .min(0, 'Amount must be positive')
        .max(999999.99, 'Amount must be less than 1,000,000'),
    })
    .optional()
    .default(undefined),
}) as yup.ObjectSchema<ClientFormData>;

const caseTypes: CaseType[] = [
  'Asylum',
  'Work Permit',
  'Naturalization',
  'Adjustment Green Card',
];

const flagEmoji = (code: string): string =>
  code
    ? code.toUpperCase().replace(/./g, (c) => String.fromCodePoint(127397 + c.charCodeAt(0)))
    : '';

const FormSkeleton: React.FC = () => (
  <Box
    sx={{
      maxWidth: 1280,
      mx: 'auto',
      px: { xs: 4, sm: 6, md: 8 },
      py: { xs: 5, md: 6 },
    }}
  >
    <Box sx={{ mb: 6, maxWidth: 640, mx: 'auto' }}>
      <Skeleton variant="text" width={60} height={24} sx={{ mb: 1.5 }} />
      <Skeleton variant="text" width={220} height={32} />
      <Skeleton variant="text" width={360} height={20} />
    </Box>
    <Box sx={{ maxWidth: 640, mx: 'auto' }}>
      <Paper
        sx={{
          p: { xs: 5, sm: 6 },
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
        }}
      >
        <Skeleton variant="text" width={160} height={22} />
        <Skeleton variant="text" width={260} height={16} sx={{ mb: 4 }} />

        <Skeleton variant="rounded" height={48} sx={{ mb: 3 }} />
        <Box sx={{ display: 'flex', gap: 3, mb: 3, flexWrap: 'wrap' }}>
          <Skeleton variant="rounded" height={48} sx={{ flex: '1 1 180px' }} />
          <Skeleton variant="rounded" height={48} sx={{ flex: '1 1 180px' }} />
        </Box>
        <Skeleton variant="rounded" height={48} sx={{ mb: 6 }} />

        <Divider sx={{ my: 6 }} />

        <Skeleton variant="rounded" height={56} sx={{ mb: 6 }} />

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Skeleton variant="rounded" width={80} height={40} />
          <Skeleton variant="rounded" width={160} height={40} />
        </Box>
      </Paper>
    </Box>
  </Box>
);

const SectionLabel: React.FC<{ children: React.ReactNode; description?: string }> = ({
  children,
  description,
}) => (
  <Box sx={{ mb: 4 }}>
    <Typography
      sx={{
        fontSize: '0.875rem',
        fontWeight: 600,
        color: 'text.primary',
      }}
    >
      {children}
    </Typography>
    {description && (
      <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mt: 0.25 }}>
        {description}
      </Typography>
    )}
  </Box>
);

const AddClient: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    // Show skeleton briefly so the route never renders blank during the
    // initial bundle parse / first paint.
    const timer = setTimeout(() => setReady(true), 200);
    return () => clearTimeout(timer);
  }, []);

  const {
    control,
    handleSubmit,
    formState: { dirtyFields },
    watch,
    setValue,
  } = useForm<ClientFormData>({
    resolver: yupResolver(schema),
    mode: 'onBlur',
    defaultValues: {
      fullName: '',
      phoneNumber: '',
      email: '',
      countryOfOrigin: '' as any,
      // initialCase is intentionally undefined here so yup's `.optional()`
      // skips inner validation when the accordion is collapsed. When the
      // accordion opens, we inject a default case object below.
      initialCase: undefined,
    },
  });

  const [includeInitialCase, setIncludeInitialCase] = useState(false);

  // Keep the form data in sync with the accordion state so yup only
  // validates the case fields when the user has actually opted in.
  useEffect(() => {
    if (includeInitialCase) {
      setValue(
        'initialCase',
        {
          type: 'Asylum',
          applicationDate: new Date().toISOString().split('T')[0],
          receiptNumber: '',
          amountCharged: undefined as unknown as number,
        },
        { shouldValidate: false },
      );
    } else {
      setValue('initialCase', undefined, { shouldValidate: false });
    }
  }, [includeInitialCase, setValue]);

  // Helper: render a green check at the right side of a valid, dirty field.
  const validAdornment = (isValid: boolean) =>
    isValid ? (
      <InputAdornment position="end">
        <CheckCircleIcon sx={{ fontSize: 18, color: 'success.main' }} />
      </InputAdornment>
    ) : null;

  const fullNameVal = watch('fullName');
  const phoneVal = watch('phoneNumber');
  const emailVal = watch('email');
  const countryVal = watch('countryOfOrigin') as string;

  const onSubmit = async (data: ClientFormData) => {
    try {
      setLoading(true);
      if (!includeInitialCase) {
        delete data.initialCase;
      }
      const clientId = await addClient(data);
      toast.success('Client saved successfully');
      navigate(`/client/${clientId}`);
    } catch (err) {
      console.error('Error adding client:', err);
      toast.error('Failed to save client');
    } finally {
      setLoading(false);
    }
  };

  if (!ready) {
    return <FormSkeleton />;
  }

  return (
    <Box
      sx={{
        maxWidth: 1280,
        mx: 'auto',
        px: { xs: 4, sm: 6, md: 8 },
        py: { xs: 5, md: 6 },
      }}
    >
      {/* Page header */}
      <Box sx={{ mb: 6, maxWidth: 640, mx: 'auto' }}>
        <Button
          startIcon={<ArrowBackIcon />}
          variant="text"
          size="small"
          onClick={() => navigate(-1)}
          sx={{ mb: 1.5, ml: -1 }}
        >
          Back
        </Button>
        <Typography
          component="h1"
          sx={{ fontSize: '1.25rem', fontWeight: 600, color: 'text.primary', mb: 0.5 }}
        >
          Add New Client
        </Typography>
        <Typography sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
          Create a new client record. Fields marked with an asterisk are required.
        </Typography>
      </Box>

      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        noValidate
        sx={{ maxWidth: 640, mx: 'auto' }}
      >
        <Paper
          sx={{
            p: { xs: 5, sm: 6 },
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
          }}
        >
          <SectionLabel description="Basic contact details for the client.">
            Client Information
          </SectionLabel>

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Controller
                name="fullName"
                control={control}
                render={({ field, fieldState }) => {
                  const isValid =
                    !fieldState.invalid && !!dirtyFields.fullName && !!fullNameVal;
                  return (
                    <TextField
                      {...field}
                      label="Full Name *"
                      fullWidth
                      placeholder="Jane Doe"
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message ?? ' '}
                      InputProps={{ endAdornment: validAdornment(isValid) }}
                    />
                  );
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="phoneNumber"
                control={control}
                render={({ field, fieldState }) => {
                  const isValid =
                    !fieldState.invalid && !!dirtyFields.phoneNumber && !!phoneVal;
                  return (
                    <TextField
                      {...field}
                      label="Phone Number *"
                      fullWidth
                      placeholder="+1-555-123-4567"
                      error={!!fieldState.error}
                      helperText={
                        fieldState.error?.message ??
                        '+1-123-456-7890 or 123-456-7890'
                      }
                      InputProps={{ endAdornment: validAdornment(isValid) }}
                    />
                  );
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="email"
                control={control}
                render={({ field, fieldState }) => {
                  const isValid =
                    !fieldState.invalid && !!dirtyFields.email && !!emailVal;
                  return (
                    <TextField
                      {...field}
                      label="Email *"
                      fullWidth
                      placeholder="jane@example.com"
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message ?? ' '}
                      InputProps={{ endAdornment: validAdornment(isValid) }}
                    />
                  );
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="countryOfOrigin"
                control={control}
                render={({ field, fieldState }) => {
                  const selected = countries.find((c) => c.name === field.value) || null;
                  const isValid = !fieldState.invalid && !!countryVal;
                  return (
                    <Autocomplete
                      options={countries}
                      value={selected}
                      onChange={(_e, val) => field.onChange(val ? val.name : '')}
                      onBlur={field.onBlur}
                      getOptionLabel={(option) => option.name}
                      isOptionEqualToValue={(opt, val) => opt.name === val.name}
                      filterOptions={(opts, state) => {
                        const q = state.inputValue.toLowerCase();
                        return opts.filter(
                          (o) =>
                            o.name.toLowerCase().includes(q) ||
                            o.code.toLowerCase().includes(q),
                        );
                      }}
                      renderOption={(props, option) => (
                        <Box component="li" {...props} sx={{ gap: 1 }}>
                          <Box component="span" sx={{ fontSize: '1.1rem' }}>
                            {flagEmoji(option.code)}
                          </Box>
                          <Box component="span" sx={{ fontSize: '0.875rem' }}>
                            {option.name}
                          </Box>
                        </Box>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Country of Origin *"
                          placeholder="Start typing a country…"
                          error={!!fieldState.error}
                          helperText={fieldState.error?.message ?? ' '}
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: selected ? (
                              <InputAdornment position="start" sx={{ ml: 0.5, mr: -0.5 }}>
                                <Box
                                  component="span"
                                  sx={{ fontSize: '1.1rem', lineHeight: 1 }}
                                >
                                  {flagEmoji(selected.code)}
                                </Box>
                              </InputAdornment>
                            ) : (
                              params.InputProps.startAdornment
                            ),
                            endAdornment: (
                              <>
                                {isValid && (
                                  <CheckCircleIcon
                                    sx={{
                                      fontSize: 18,
                                      color: 'success.main',
                                      mr: 0.5,
                                    }}
                                  />
                                )}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                    />
                  );
                }}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 6 }} />

          {/* Initial Case (Optional) */}
          <Accordion
            expanded={includeInitialCase}
            onChange={(_e, expanded) => setIncludeInitialCase(expanded)}
            elevation={0}
            disableGutters
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              '&:before': { display: 'none' },
              '&.Mui-expanded': { my: 0 },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                px: 3,
                minHeight: 56,
                '& .MuiAccordionSummary-content': {
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                },
              }}
            >
              <Box>
                <Typography
                  sx={{ fontSize: '0.875rem', fontWeight: 600, color: 'text.primary' }}
                >
                  Initial Case
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                  Optional — add a first case while creating the client.
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails
              sx={{
                p: 4,
                pt: 1,
                borderTop: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Controller
                    name="initialCase.type"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        value={field.value ?? 'Asylum'}
                        select
                        label="Case Type"
                        fullWidth
                        helperText=" "
                      >
                        {caseTypes.map((type) => (
                          <MenuItem key={type} value={type}>
                            {type}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Controller
                    name="initialCase.applicationDate"
                    control={control}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        value={field.value ?? ''}
                        type="date"
                        label="Application Date"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message ?? ' '}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Controller
                    name="initialCase.receiptNumber"
                    control={control}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        value={field.value ?? ''}
                        label="Receipt Number"
                        fullWidth
                        placeholder="ABC-123-456-789"
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message ?? ' '}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Controller
                    name="initialCase.amountCharged"
                    control={control}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        value={field.value ?? ''}
                        type="number"
                        label="Amount Charged (USD) *"
                        placeholder="0.00"
                        fullWidth
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">$</InputAdornment>
                          ),
                        }}
                        inputProps={{ step: '0.01', min: 0 }}
                        error={!!fieldState.error}
                        helperText={
                          fieldState.error?.message ??
                          'Required when adding an initial case'
                        }
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* Form actions */}
          <Box
            sx={{
              mt: 6,
              display: 'flex',
              justifyContent: { xs: 'stretch', sm: 'flex-end' },
              flexDirection: { xs: 'column-reverse', sm: 'row' },
              gap: 2,
            }}
          >
            <Button
              type="button"
              variant="text"
              onClick={() => navigate(-1)}
              disabled={loading}
              sx={{ minHeight: 40 }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading}
              sx={{ minHeight: 40, minWidth: 160 }}
              startIcon={
                loading ? <CircularProgress size={16} sx={{ color: '#FFFFFF' }} /> : null
              }
            >
              {loading ? 'Saving…' : 'Save Client'}
            </Button>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default AddClient;
